import type WhiteboardController from '../controllers/WhiteboardController';
import UndoManager from './UndoManager';
import Serialization, { type TableKey, type DocData } from './Serialization';
import Debug from 'debug';
import otWhiteboard from '@aha-app/ot-whiteboard';
import { fireCreatedDeep } from '../utils/objects';
import { type OtWhiteboardOp } from '../ot-whiteboard';
import {
  type IDataStoreRecordBehaviors,
  type KeyPath,
} from './DataStoreRecordBehaviors';

const debug = Debug('whiteboard:client');

class OpProcessingError extends Error {}

const allowedKeys = ['p'];
export const redactOp = (op: OtWhiteboardOp) =>
  Object.fromEntries(
    Object.entries(op).map(([key, value]) => [
      key,
      allowedKeys.includes(key) ? value : '[redacted]',
    ])
  );

// Base class for clients that defines the public API.
class ClientBase {
  undoManager: UndoManager;
  doc: { data: DocData };
  discardOutgoingOps: boolean = false;

  constructor(
    public readonly controller: WhiteboardController,
    public readonly isLoopbackClient: boolean
  ) {
    this.undoManager = new UndoManager(controller, this);
  }

  destroy() {}
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialize(initialVersion: number, initialData: DocData) {}

  // Send an operation to the remote.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  sendOp(op: OtWhiteboardOp[]) {}

  // Whether the client is currently processing operations that arrived from
  // the remote (and so should not be sent back to the remote).
  processingRemoteOps = false;
  // Manage a queue of pending operations.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removePendingOperationsFor(op) {}
  sendPendingOperations() {}

  // An object was added locally - send to remote.
  addObject(tableKey: string, object: IDataStoreRecordBehaviors) {
    if (this.processingRemoteOps) {
      return;
    }

    debug(`addObject ${object.uniqueId()}`);
    this.queueOp(
      otWhiteboard.type.opCreate(tableKey, object.uniqueId(), object.toJSON())
    );
  }

  // An object was removed locally - remove from remote.
  removeObject(tableKey: string, object: IDataStoreRecordBehaviors) {
    if (this.processingRemoteOps) {
      return;
    }

    const objectId = object.uniqueId();

    debug(`removeObject ${objectId}`);

    this.queueOp(
      otWhiteboard.type.opDelete(tableKey, objectId, object.toJSON())
    );
  }

  // An object was modified locally - send to remote.
  setObjectProperty(
    path: string[],
    value: unknown,
    oldValue: unknown,
    force = false
  ) {
    const tableKey = path[0];
    const objectId = path[1];

    // Don't send operations that just came from a remote system back out again.
    if (!force && this.processingRemoteOps) return;

    // Make sure the object still exists. The remote may have deleted it while
    // the local user was operating on it (most commonly during a drag).
    //
    // Ideally this would be solved by having the local code track the sharedb
    // document version and if it falls behind the version of applied remote
    // operations, then transform local operations by the remote ones before
    // sending. But this trivial fix seems to sidestep that problem with a lot
    // less complexity.
    if (!this.controller.dataStoreForKey(tableKey).findRecordById(objectId)) {
      return;
    }

    // Ensure that any undefined values are changed to null since undefined keys
    // are stripped by JSON.stringify.
    this.queueOp(
      otWhiteboard.type.opUpdateProperty(
        path,
        oldValue === undefined ? null : oldValue,
        value === undefined ? null : value
      )
    );
  }

  checkCanDiscardOp(
    op: OtWhiteboardOp | OtWhiteboardOp[],
    shouldRedactOp = true
  ) {
    const cannotBeDiscarded = (o: OtWhiteboardOp) => 'do' in o || 'co' in o;
    let disallowedOps: typeof op | ReturnType<typeof redactOp> | undefined;
    if (Array.isArray(op)) {
      disallowedOps = op
        .filter(cannotBeDiscarded)
        .map(o => (shouldRedactOp ? redactOp(o) : o));
    } else if (cannotBeDiscarded(op)) {
      disallowedOps = shouldRedactOp ? redactOp(op) : op;
    }
    if (disallowedOps !== undefined) {
      const errMsg =
        '"create object" and "delete object" ops cannot be discarded as they will cause sync issues. Do not issue these ops while `discardOutgoingOps` is `true`';
      console.error(errMsg, { disallowedOps });
      throw new Error(errMsg);
    }
  }

  queueOp(op: OtWhiteboardOp | OtWhiteboardOp[]) {
    if (this.discardOutgoingOps) {
      this.checkCanDiscardOp(op, false);
      debug(`Discarding outgoing op`, op);
      return;
    }
    if (!op) return;
    if (!Array.isArray(op)) op = [op];

    this.undoManager.saveOp(op);
    this.controller.actionSetIsEmpty();
  }

  // Apply a shareDB operation that arrived from a remote.
  applyRemoteOperation(op: OtWhiteboardOp[], options = {}) {
    try {
      this.processingRemoteOps = true;

      this.applyOperation(op, options);

      this.controller.canvas.requestRenderAll();

      if (this.controller.fuzzer?.running()) {
        Serialization.validateDoc(this.controller, this.doc.data);
      }
    } catch (e) {
      console.log('*** Data before error:');
      console.log(this.doc.data);
      console.log('*** Failed op:');
      console.log(op);
      this.controller.actionHandleError(e);
    } finally {
      this.processingRemoteOps = false;
    }
  }

  dataStoreForOp(op: OtWhiteboardOp) {
    return this.controller.dataStoreForKey(op.p[0]);
  }

  getTimesSinceLastRemoteOp() {
    return 'Times since last remote ops: Not implemented for this client';
  }

  // Apply operation to whiteboard. This implementation is a hard-coded
  // decoder of ot-whiteboard operations. It takes remote operations and
  // makes the canvas reflect those changes.
  //
  // The specification for this format is here: https://github.com/aha-app/ot-whiteboard
  applyOperation(ops: OtWhiteboardOp[], options: Record<string, unknown> = {}) {
    let zChange = false;
    const objectsToAdd: fabric.Object[] = [];

    for (const op of ops) {
      if ('co' in op) {
        // Create object.
        if (op.p[0] === 'objects') {
          const obj = Serialization.restoreObject(this.controller, op.co);
          objectsToAdd.push(obj);
        } else {
          Serialization.restoreRecord(
            this.controller,
            op.p[0] as TableKey,
            op.p[1],
            op.co
          );
        }
      } else if ('do' in op) {
        const obj = this.dataStoreForOp(op).findRecordById(op.p[1]);
        if (!obj) {
          throw new OpProcessingError(
            `Can't find object to remove from "${op.p[0]}". ID: ${
              op.p[1]
            }. ${this.getTimesSinceLastRemoteOp()}`
          );
        }

        // Special handling for objects.
        if (op.p[0] === 'objects') {
          // Remove from any group.
          if (obj.simpleGroup) {
            obj.simpleGroup.removeWithUpdate(obj);
          }

          // Remove from the canvas.
          this.controller.canvas.remove(obj);
          obj.deleted(this.controller);
          if (options.isSource) {
            obj.fire('deleted');
          }

          // Don't add if it was just removed.
          const indexToRemove = objectsToAdd.indexOf(obj.id);
          if (indexToRemove >= 0) {
            objectsToAdd.splice(indexToRemove, 1);
          }
        } else {
          this.dataStoreForOp(op).removeRecord(obj);
        }

        // Remove any pending operations for the deleted object.
        this.removePendingOperationsFor(op);
      } else if ('cp' in op) {
        const dataStore = this.dataStoreForOp(op);
        const obj = dataStore.findRecordById(op.p[1]);
        if (!obj) {
          throw new Error(
            `Unable to find object ${op.p[1]} to modify in datastore '${
              dataStore._tableKey
            }'. ${this.getTimesSinceLastRemoteOp()}`
          );
        }

        let path: KeyPath = op.p.slice(2);
        if (path.length === 1) {
          path = path[0];
        }

        obj.setFromRemote(path, op.cp, options);

        if (op.p[2] === 'z') {
          zChange = true;
        }
      } else {
        throw new Error(`Unhandled operation: ${JSON.stringify(op)}`);
      }
    }

    // Add objects to canvas.
    for (const obj of objectsToAdd) {
      if (obj.simpleGroup) continue;
      this.controller.canvas.addObjects([obj]);
      // If this is an undo/redo op, fire created on objects and any
      // nested objects within groups. This allows us to hide/unhide comments
      // in the side panel
      if (options.isSource) {
        fireCreatedDeep(obj);
      }
    }

    if (zChange) {
      this.controller.canvas.collectionNeedsSort = true;
    }

    this.controller.actionSetIsEmpty();
  }

  needsCatchup() {
    return false;
  }
}

export default ClientBase;
